import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { Users, Clock, Phone, Calendar, MessageSquare, Globe, FileText, Settings, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { JourneyCard } from '@/components/cards/JourneyCard';
import { FloatingIslandCard } from '@/components/cards/FloatingIslandCard';
import { HolographicCard } from '@/components/cards/HolographicCard';

const PatientAccessAutomation = () => {
  const [typingCardIndex, setTypingCardIndex] = React.useState(-1);
  const [completedCards, setCompletedCards] = React.useState<number[]>([]);
  
  const keyCapabilities = [
    {
      icon: Calendar,
      title: "24/7 Appointment Management",
      description: "Eliminate hold times with automated scheduling, rescheduling, cancellations, and confirmations.",
      number: "01"
    },
    {
      icon: Phone,
      title: "Switchboard (Provider Triage & FAQ)",
      description: "AI triages calls, answers FAQs by provider and clinical context, and routes complex requests to live staff.",
      number: "02"
    },
    {
      icon: Users,
      title: "Self-Scheduling",
      description: "Offer personalized booking via phone, web portal, email, or SMS—meeting patients where they are.",
      number: "03"
    },
    {
      icon: FileText,
      title: "Prescription Refills & Renewals",
      description: "Enable refill requests and renewals through natural voice or SMS interactions.",
      number: "04"
    },
    {
      icon: MessageSquare,
      title: "SMS Recovery & Follow-Ups",
      description: "Proactively recover unscheduled or missed appointments with automated SMS follow-ups and reminders.",
      number: "05"
    }
  ];

  const additionalFeatures = [
    {
      icon: Globe,
      title: "Multilingual Support",
      description: "Engage diverse patient populations in their preferred languages seamlessly.",
      gradient: "from-primary/20 to-accent/20"
    },
    {
      icon: FileText,
      title: "Patient Case Creation",
      description: "Automatically open and manage patient cases based on interactions and clinical triggers.",
      gradient: "from-accent/20 to-secondary/20"
    },
    {
      icon: Settings,
      title: "Bespoke Workflow Automation",
      description: "Customize and extend AI-driven workflows to meet unique organizational needs.",
      gradient: "from-secondary/20 to-primary/20"
    }
  ];

  const handleCardComplete = (index: number) => {
    setCompletedCards(prev => [...prev, index]);
    if (index < keyCapabilities.length - 1) {
      setTimeout(() => {
        setTypingCardIndex(index + 1);
      }, 300);
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setTypingCardIndex(0);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Patient Access Automation - Seamless Healthcare Access | Scienza Health</title>
        <meta 
          name="description" 
          content="Deploy intelligent AI agents to automate patient access across all touchpoints, ensuring seamless scheduling, communication, and support 24/7." 
        />
      </Helmet>

      <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--gradient-depth)' }}>
        <NeuralBackground />
        
        {/* Hero Section */}
        <section className="relative z-10 pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12 sm:mb-16"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-primary/10 backdrop-blur-md border border-primary/20 rounded-full mb-6 sm:mb-8">
                <Users className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-primary" />
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Patient Access
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-1 sm:mt-2">
                  Automation
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl lg:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-10 px-2">
                Deploy intelligent Digital Human® to automate patient access across all touchpoints, ensuring seamless scheduling, communication, and support.
              </p>

              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-8 sm:px-12 py-3 sm:py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-base sm:text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25 min-h-[44px]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                <span className="whitespace-nowrap">Discover Patient Access Automation</span>
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 md:p-12 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center">
                  <SynapticText 
                    text="Intelligent Patient Access Solutions" 
                    delay={300}
                  />
                </h2>
                <SynapticText 
                  text="Deploy intelligent AI agents to automate patient access across all touchpoints, ensuring seamless scheduling, communication, and support."
                  delay={1200}
                  className="text-lg text-white leading-relaxed"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Key Capabilities Section */}
        <section className="relative z-10 py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
                <SynapticText 
                  text="Key Capabilities" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Comprehensive automation for every patient touchpoint"
                delay={1200}
                className="text-lg sm:text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            {/* Mobile: Stack cards vertically, Tablet: 2 columns, Desktop: 5 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
              {keyCapabilities.map((capability, index) => (
                <JourneyCard
                  key={capability.title}
                  icon={capability.icon}
                  title={capability.title}
                  description={capability.description}
                  number={capability.number}
                  index={index}
                  shouldStartTyping={index === typingCardIndex}
                  onComplete={() => handleCardComplete(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Additional Features Section */}
        <section className="relative z-10 py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
                <SynapticText 
                  text="Advanced Features" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Enhanced capabilities for comprehensive patient engagement"
                delay={800}
                className="text-lg sm:text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            {/* Mobile: Stack vertically, Tablet/Desktop: 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-10">
              {additionalFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.15,
                    ease: "easeOut"
                  }}
                  viewport={{ once: true }}
                >
                  <HolographicCard
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    index={index}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
                Transform Patient Access Today
              </h2>
              <p className="text-lg sm:text-xl text-white/80 mb-8 sm:mb-10 max-w-3xl mx-auto px-2">
                Streamline your patient access workflow with intelligent automation that works 24/7. 
                Reduce wait times, improve satisfaction, and free up your staff for higher-value care.
              </p>
              
              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-8 sm:px-12 py-3 sm:py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-base sm:text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25 min-h-[44px]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                Get Started Today
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PatientAccessAutomation;